import { Input, Table } from 'reactstrap';
import { FSM_STATES, REQUIRED_FSM_STATES, type FsmState } from '../../constants/fsmStates';
import { TtsPlayer } from '../tts/TtsPlayer';

export interface PromptsGridProps {
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  readOnly?: boolean;
}

const DEMO_VARS: Record<string, string> = {
  '{location}': 'A01',
  '{secondary_location}': 'B07-piano2',
  '{qty}': '20',
  '{qty_requested}': '20',
  '{qty_picked}': '20',
  '{stock_remaining}': '8',
  '{display_name}': 'ORD-2026-0042 · Libreria Centrale',
  '{item}': 'pere',
  '{item_name}': 'pere',
  '{item_discriminator}': '823',                // ultime N cifre del codice prodotto
  '{discriminator_digits_requested}': 'tre',    // quante cifre il sistema chiede
  '{item_info}': 'Produttore: Az. Agricola Rossi',
};

function substitute(text: string): string {
  let out = text;
  for (const [k, v] of Object.entries(DEMO_VARS)) out = out.split(k).join(v);
  return out;
}

export function PromptsGrid({ value, onChange, readOnly = false }: PromptsGridProps) {
  // Fully controlled: read from `value` directly. Earlier impl used a local
  // useState(() => ({...value})) lazy-initialized once at mount, which never
  // synced when the parent loaded existing template data via useEffect — so
  // editing an existing template showed empty prompts even when the data was
  // populated in the parent state.
  const missing = REQUIRED_FSM_STATES.filter((s) => !value[s] || value[s].trim() === '');

  const update = (state: FsmState, prompt: string) => {
    if (readOnly) return;
    onChange({ ...value, [state]: prompt });
  };

  return (
    <div>
      {missing.length > 0 && (
        <div className="alert alert-warning py-2 px-3 small mb-2">
          Prompt obbligatori mancanti: {missing.join(', ')}.
        </div>
      )}
      <div className="table-responsive">
      <Table className="table-sm align-middle table-striped mb-0" role="grid">
        <thead>
          <tr>
            <th style={{ width: 220 }}>Stato FSM</th>
            <th>Prompt</th>
            <th style={{ width: 96 }}>▶</th>
          </tr>
        </thead>
        <tbody>
          {FSM_STATES.map((state) => {
            const text = value[state] ?? '';
            const required = REQUIRED_FSM_STATES.includes(state);
            const empty = text.trim() === '';
            return (
              <tr key={state}>
                <td>
                  <code style={{ background: 'var(--vp-neutral-800)', color: 'var(--vp-sidebar-fg)', padding: '2px 6px', borderRadius: 2, fontSize: 11 }}>
                    {state}
                  </code>
                  {required && <span style={{ color: 'var(--vp-danger)', marginLeft: 4 }}>*</span>}
                </td>
                <td>
                  <Input
                    value={text}
                    onChange={(e) => update(state, e.target.value)}
                    placeholder={empty && required ? '— mancante —' : ''}
                    aria-label={`prompt ${state}`}
                    invalid={empty && required}
                    disabled={readOnly}
                  />
                </td>
                <td>{!empty && <TtsPlayer text={substitute(text)} />}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      </div>
    </div>
  );
}
